import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { io } from 'socket.io-client';
import {
  AUTOMATION_NEW_AGENT_TOKEN,
  AUTOMATION_NEW_AGENT_URL,
  AUTOMATION_NEW_SOCKET_PATH,
  RUN_LABEL,
} from '../constants';
import { fetchEnvironmentList } from '../../../utils/api/environment_apis';
import { getVariableListData, getVariablesList } from '../../../utils/api/Variable_apis';
import { getExecutionFilesForDropdown } from '../../../utils/api/executionFileApis';
import { STATUS } from '../../../utils/api/sharedfunction';
import {
  buildVariablePayloadForAgentStrict,
  collectSecretPlaintexts,
  convertIdsToDisplay,
  extractVariableIds,
  instructionUsesSecretVariable,
  resolveVariablesUsedRow,
  scrubSecretPlaintexts,
} from '../../../utils/automation/variableUtils';
 
const GENERIC_SECRET_MIND = 'Details hidden — a secret value could not be safely masked here.';
 
/** Narrative beats the Story view always keeps, keyed off agent graph nodes. */
const THOUGHT_BEAT_BY_NODE = {
  orchestrate_step: 'plan',
  evaluate: 'check',
  recover: 'recover',
};
 
const THOUGHT_BEAT_TITLE = {
  plan: 'Deciding what to do next',
  check: 'Checking whether the step worked',
  recover: 'Step did not work — trying another way',
  think: 'Working out the next step',
};
 
const L5_CHIP_MESSAGES = {
  VAR_INVALID_PAYLOAD: 'Invalid variable payload — check instructions and try again.',
  VAR_S2S_NOT_CONFIGURED: 'Variable service is not configured on the agent.',
  VAR_S2S_UNREACHABLE: 'Cannot reach variable service — is ATM up?',
  VAR_UNAUTHORIZED: 'Not authorized to load these variables.',
  VAR_ENV_NOT_FOUND: 'Selected environment was not found.',
  VAR_NOT_FOUND: 'A referenced variable was not found.',
  VAR_ENV_VALUE_MISSING: 'A variable has no value in the selected environment.',
  VAR_DECRYPT_FAILED: 'Could not decrypt a secret variable.',
  VAR_UNRESOLVED: 'Variables could not be resolved for this run.',
  VALIDATE_BLOCKING: 'Fix the instruction issues below, then try again.',
  VALIDATE_SCHEMA: 'A step is missing required fields — check the instruction payload.',
};
 
function formatElapsed(ms) {
  const totalSec = Math.max(0, Math.floor(ms / 1000));
  const h = String(Math.floor(totalSec / 3600)).padStart(2, '0');
  const m = String(Math.floor((totalSec % 3600) / 60)).padStart(2, '0');
  const s = String(totalSec % 60).padStart(2, '0');
  return `${h}:${m}:${s}`;
}
 
function toTimeString() {
  return new Date().toTimeString().slice(0, 8);
}
 
function resolveScreenshotUrl(url) {
  if (!url) return null;
  if (/^https?:\/\//i.test(url) || url.startsWith('data:') || url.startsWith('blob:')) return url;
  const base = AUTOMATION_NEW_AGENT_URL.replace(/\/$/, '');
  return `${base}${url.startsWith('/') ? '' : '/'}${url}`;
}
 
function instructionsToPayload(instructions) {
  return instructions
    .filter((inst) => inst?.text?.trim())
    .map((inst, index) => ({
      id: index + 1,
      text: inst.text.trim(),
      type: inst.type || 'ACTION',
    }));
}
 
function createSessionId(prefix = 'anew') {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}
 
function normalizeStatus(raw) {
  const value = String(raw || '').toLowerCase();
  if (!value) return 'pending';
  if (['passed', 'pass', 'completed', 'complete', 'done', 'success', 'ok'].some((s) => value.includes(s))) {
    return 'completed';
  }
  if (['running', 'in_progress', 'progress', 'active', 'executing'].some((s) => value.includes(s))) {
    return 'running';
  }
  if (['fail', 'error', 'cancelled', 'aborted'].some((s) => value.includes(s))) {
    return 'error';
  }
  return 'pending';
}
 
function mapHierarchicalToInstructions(steps) {
  return (steps || []).map((step, index) => {
    const childrenSource = step?.children || step?.actions || step?.steps || [];
    const children = childrenSource.map((child, childIndex) => ({
      id: `live-c-${index}-${childIndex}`,
      number: `${index + 1}.${childIndex + 1}`,
      text: child?.text || child?.description || child?.action || child?.tool || `Action ${childIndex + 1}`,
      status: normalizeStatus(
        child?.status || child?.state || (child?.passed === false ? 'error' : child?.passed ? 'completed' : '')
      ),
    }));
    const status = normalizeStatus(step?.status || step?.state);
    return {
      id: String(step?.id ?? `live-${index + 1}`),
      number: index + 1,
      text: step?.text || step?.instruction || step?.name || `Instruction ${index + 1}`,
      status,
      expanded: status === 'running' || children.length > 0,
      children,
    };
  });
}
 
function isRunSuccessful(result) {
  if (result?.success === true) return true;
  if (result?.success === false) return false;
  const status = String(result?.test_status || result?.overallStatus || result?.status || '').toLowerCase();
  return ['passed', 'pass', 'success', 'completed', 'ok'].includes(status);
}
 
/** Collect ${_id:uuid} refs from instruction texts. */
function collectReferencedVariableIds(instructions = []) {
  const ids = new Set();
  for (const inst of instructions || []) {
    const text = typeof inst === 'string' ? inst : inst?.text || '';
    extractVariableIds(text).forEach((id) => ids.add(String(id).trim()));
  }
  return [...ids];
}
 
/**
 * Preflight before talking to the agent — gate early so secrets never leave the browser
 * and missing env/refs fail closed in the UI.
 */
function buildPreflightMessages({
  instructions,
  environmentId,
  variablesList,
  variableDescriptions,
  agentHealthy,
}) {
  const messages = [];
  const refs = collectReferencedVariableIds(instructions);
  if (agentHealthy === false) {
    messages.push(`Cannot connect to agent at ${AUTOMATION_NEW_AGENT_URL}.`);
  }
  if (refs.length > 0 && !environmentId) {
    messages.push('Select an environment — this run uses environment variables.');
  }
  for (const id of refs) {
    const known =
      (variablesList || []).some((v) => String(v.id || v.variable_id) === String(id)) ||
      Boolean(variableDescriptions?.[id] || variableDescriptions?.[String(id).toLowerCase()]);
    if (!known) {
      messages.push('Unknown variable in instructions…');
      break;
    }
  }
  if (environmentId && refs.length > 0) {
    for (const id of refs) {
      const variable = (variablesList || []).find((v) => String(v.id || v.variable_id) === String(id));
      if (!variable) continue;
      const key = variable.key || variable.name || id;
      let hasValue = variable.value != null && variable.value !== '';
      if (Array.isArray(variable.environment_values)) {
        const match = variable.environment_values.find(
          (ev) => String(ev.environment_id || ev.environmentId) === String(environmentId)
        );
        hasValue = match != null && match.value != null && match.value !== '';
      }
      // Ciphertext / masked placeholder still counts as "has value" for env presence
      if (!hasValue) {
        messages.push(`Variable "${key}" has no value in the selected environment.`);
      }
    }
  }
  return messages;
}
 
const DEFAULT_INSTRUCTIONS = [
  {
    id: 'inst-1',
    number: 1,
    text: '',
    status: 'pending',
    expanded: false,
    children: [],
  },
];
 
const PLAN_STORAGE_KEY = 'automationNew.lastPlanId';
 
function planStorageKeyFor(projectId) {
  return projectId ? `${PLAN_STORAGE_KEY}.${projectId}` : PLAN_STORAGE_KEY;
}
 
function readStoredPlanId(projectId) {
  try {
    return sessionStorage.getItem(planStorageKeyFor(projectId)) || '';
  } catch {
    return '';
  }
}
 
function writeStoredPlanId(projectId, id) {
  if (!id) return;
  try {
    sessionStorage.setItem(planStorageKeyFor(projectId), id);
  } catch {
    /* ignore */
  }
}
 
/**
 * Full qa-path-automation-agent integration for Automation New:
 * record (executeTest), replay (replayTestPlanJson), stop, savePlan + all live events.
 */
export default function useAutomationNewAgent() {
  const { project_id: projectId } = useParams();
  const userId = useSelector((state) => state.auth.user?.id);
  const orgId = useSelector((state) => state.auth.user?.org_id);

  const socketRef = useRef(null);
  const startedAtRef = useRef(null);
  const timerRef = useRef(null);
  const actionCountRef = useRef(0);
  const sessionIdRef = useRef(null);
  const secretScrubValuesRef = useRef([]);
  const recentStepShotUrlsRef = useRef(new Set());
  // Guards the disconnect handler below: false while a run is in flight, true once it has
  // a definitive outcome (testComplete/stop). Lets us tell "socket died mid-run" apart from
  // the expected disconnect that follows a normal finish, so a dropped connection fails fast
  // instead of leaving the UI stuck on "Running" forever.
  const runEndedRef = useRef(true);
 
  const [instructions, setInstructions] = useState(DEFAULT_INSTRUCTIONS);
  const [timeline, setTimeline] = useState([]);
  const [timelineFilter, setTimelineFilter] = useState('story');
  const [runLabel, setRunLabel] = useState(RUN_LABEL.IDLE);
  const [elapsed, setElapsed] = useState('00:00:00');
  const [connectionError, setConnectionError] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [agentHealthy, setAgentHealthy] = useState(null);
  const [latestScreenshot, setLatestScreenshot] = useState(null);
  const [screenshots, setScreenshots] = useState([]);
  const [tokenUsage, setTokenUsage] = useState(null);
  const [lastResult, setLastResult] = useState(null);
  const [resultModalOpen, setResultModalOpen] = useState(false);
  const [planId, setPlanId] = useState('');
  const [environments, setEnvironments] = useState([]);
  const [environmentId, setEnvironmentId] = useState('');
  const [variablesList, setVariablesList] = useState([]);
  const [variableSuggestions, setVariableSuggestions] = useState([]);
  const [variableDescriptions, setVariableDescriptions] = useState({});
  const [fileSuggestions, setFileSuggestions] = useState([]);
  const [fileDescriptions, setFileDescriptions] = useState({});
  const [variablePayloadText, setVariablePayloadText] = useState('{}');
  const [variablesUsedRows, setVariablesUsedRows] = useState([]);
  const [preflightMessages, setPreflightMessages] = useState([]);
  const [executorMeta, setExecutorMeta] = useState({
    instructionCurrent: 0,
    instructionTotal: 0,
    actionCurrent: 0,
    actionTotal: 0,
    browser: null,
    viewport: null,
    os: null,
    targetLabel: null,
    confidence: null,
    pageUrl: null,
  });
  const [stats, setStats] = useState({
    stepsCurrent: 0,
    stepsTotal: 0,
    actionsCurrent: 0,
    actionsTotal: 0,
    retries: 0,
    variablesUsed: 0,
  });
 
  const isRunning = runLabel === RUN_LABEL.RUNNING || runLabel === RUN_LABEL.CONNECTING;
 
  const appendTimeline = useCallback((entry) => {
    setTimeline((prev) => {
      const last = prev[prev.length - 1];
      // Backend re-emits identical thoughts/logs during retries — drop exact consecutive
      // repeats so the Agent Mind timeline doesn't fill up with the same line over and over.
      if (
        last &&
        last.kind === entry.kind &&
        last.title === entry.title &&
        (last.body || '') === (entry.body || '')
      ) {
        return prev;
      }
      return [
        ...prev,
        {
          id: `tl-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
          time: toTimeString(),
          status: 'running',
          ...entry,
        },
      ];
    });
  }, []);

  const settleRunningTimeline = useCallback((status) => {
    setTimeline((prev) =>
      prev.map((entry) => (entry.status === 'running' ? { ...entry, status } : entry))
    );
  }, []);

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);
 
  const startTimer = useCallback(() => {
    clearTimer();
    startedAtRef.current = Date.now();
    setElapsed('00:00:00');
    timerRef.current = setInterval(() => {
      setElapsed(formatElapsed(Date.now() - startedAtRef.current));
    }, 1000);
  }, [clearTimer]);
 
  const teardownSocket = useCallback(() => {
    const socket = socketRef.current;
    if (socket) {
      socket.removeAllListeners();
      socket.disconnect();
      socketRef.current = null;
    }
    setIsConnected(false);
  }, []);
 
  const checkAgentHealth = useCallback(async () => {
    try {
      const res = await fetch(`${AUTOMATION_NEW_AGENT_URL}/health`, { method: 'GET' });
      setAgentHealthy(res.ok);
      return res.ok;
    } catch {
      setAgentHealthy(false);
      return false;
    }
  }, []);
 
  useEffect(() => {
    checkAgentHealth();
    const id = setInterval(checkAgentHealth, 15000);
    return () => clearInterval(id);
  }, [checkAgentHealth]);
 
  useEffect(() => {
    let cancelled = false;
    setEnvironments([]);
    setEnvironmentId('');
    setPlanId(readStoredPlanId(projectId));
 
    (async () => {
      const [envRes, varValuesRes, varListRes, fileRes] = await Promise.all([
        fetchEnvironmentList().catch(() => null),
        getVariablesList().catch(() => null),
        getVariableListData().catch(() => null),
        getExecutionFilesForDropdown().catch(() => null),
      ]);
      if (cancelled) return;
      if (envRes?.status === STATUS.SUCCESS) {
        const list = envRes.data || [];
        setEnvironments(list);
        setEnvironmentId(list[0]?.id || list[0]?.environment_id || '');
      } else {
        setEnvironments([]);
        setEnvironmentId('');
      }
      if (varValuesRes?.status === STATUS.SUCCESS || Array.isArray(varValuesRes?.data)) {
        setVariablesList(varValuesRes?.data || []);
      }
      if (varListRes?.status === STATUS.SUCCESS) {
        const payload = varListRes.data || {};
        setVariableSuggestions(payload.data || payload.suggestions || []);
        setVariableDescriptions(payload.description || payload.descriptions || {});
      }
      if (fileRes?.status === STATUS.SUCCESS) {
        setFileSuggestions(fileRes.data?.suggestions || []);
        setFileDescriptions(fileRes.data?.descriptions || {});
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [projectId]);
 
  const refreshFiles = useCallback(async () => {
    const fileRes = await getExecutionFilesForDropdown().catch(() => null);
    if (fileRes?.status === STATUS.SUCCESS) {
      setFileSuggestions(fileRes.data?.suggestions || []);
      setFileDescriptions(fileRes.data?.descriptions || {});
    }
  }, []);
 
  useEffect(() => () => {
    clearTimer();
    teardownSocket();
  }, [clearTimer, teardownSocket]);
 
  const resolveVariablePayload = useCallback(() => {
    // security: secrets — never send plaintext values; metadata/refs only
    const strict = buildVariablePayloadForAgentStrict(
      instructions,
      variablesList,
      variableDescriptions
    );
    // Settings JSON cannot smuggle secrets — ignore any override that includes values
    try {
      const parsed = JSON.parse(variablePayloadText || '{}');
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        const hasMapValues =
          parsed.variableMap &&
          typeof parsed.variableMap === 'object' &&
          Object.keys(parsed.variableMap).length > 0;
        const looksLikeFlatValues =
          !parsed.variableMetadata &&
          Object.keys(parsed).some((k) => k !== 'variableMap' && k !== 'variableMetadata');
        if (hasMapValues || looksLikeFlatValues) {
          // Drop value overrides; keep metadata-only contract
          return strict;
        }
        if (parsed.variableMetadata && typeof parsed.variableMetadata === 'object') {
          return {
            variableMap: {},
            variableMetadata: { ...strict.variableMetadata, ...parsed.variableMetadata },
          };
        }
      }
    } catch {
      // fall through to strict
    }
    return strict;
  }, [instructions, variableDescriptions, variablePayloadText, variablesList]);
 
  const preflight = useMemo(
    () =>
      buildPreflightMessages({
        instructions,
        environmentId,
        variablesList,
        variableDescriptions,
        agentHealthy,
      }),
    [agentHealthy, environmentId, instructions, variableDescriptions, variablesList]
  );
 
  useEffect(() => {
    setPreflightMessages(preflight);
  }, [preflight]);
 
  const runBlocked = preflight.length > 0;
 
  const sanitizeMindText = useCallback(
    (text) => {
      // security: secrets — mask the value, keep the sentence readable
      const secrets = secretScrubValuesRef.current;
      const masked = scrubSecretPlaintexts(text, secrets);
      // Safety net: only withhold text when a secret survived masking
      if (secrets?.length && secrets.some((value) => String(masked).includes(value))) {
        return GENERIC_SECRET_MIND;
      }
      // Mind should show ${ENV.name} / ${PROJECT.name}, not ${_id:uuid}
      return convertIdsToDisplay(masked, variablesList, variableDescriptions);
    },
    [variableDescriptions, variablesList]
  );
 
  const toDisplayTask = useCallback(
    (task) => {
      if (!task || typeof task !== 'object') return task || null;
      if (!task.text) return task;
      return {
        ...task,
        text: convertIdsToDisplay(task.text, variablesList, variableDescriptions),
      };
    },
    [variableDescriptions, variablesList]
  );
 
  const bindListeners = useCallback(
    (socket) => {
      socket.on('log', (data) => {
        const level = data?.level || 'info';
        const category = data?.category || data?.group || 'progress';
        const kind = category === 'thought' ? 'thought' : category === 'action' ? 'action' : 'log';
        const task = toDisplayTask(data?.task);
        const taskText = task?.text || '';
        const usesSecret = instructionUsesSecretVariable(
          taskText,
          variablesList,
          variableDescriptions
        );
        const rawMessage = sanitizeMindText(data?.message || 'Log');
        let title = rawMessage;
        let body = '';
        let validationIssue = null;
 
        if (task?.kind === 'validation_issue') {
          const severity = task.severityLabel || 'Issue';
          const where =
            task.where ||
            (typeof task.stepIndex === 'number' ? `Step ${task.stepIndex}` : 'Whole scenario');
          title = `${severity} · ${where} — ${task.title || 'Instruction issue'}`;
          body = [
            task.message && sanitizeMindText(task.message),
            task.fixHint && `How to fix: ${sanitizeMindText(task.fixHint)}`,
            task.question && `Please clarify: ${sanitizeMindText(task.question)}`,
          ]
            .filter(Boolean)
            .join('\n');
          validationIssue = {
            kind: 'validation_issue',
            type: task.severity || task.type || 'suggestion',
            severityLabel: severity,
            title: task.title || 'Instruction issue',
            index: typeof task.stepIndex === 'number' ? task.stepIndex : task.index,
            scope: typeof task.stepIndex === 'number' || typeof task.index === 'number' ? 'instruction' : 'scenario',
            message: task.message ? sanitizeMindText(task.message) : '',
            fix_hint: task.fixHint ? sanitizeMindText(task.fixHint) : undefined,
            question: task.question ? sanitizeMindText(task.question) : undefined,
            rule: task.rule,
          };
        } else if (String(rawMessage).includes('\n')) {
          const lines = String(rawMessage)
            .split('\n')
            .map((line) => line.trim())
            .filter(Boolean);
          title = lines[0] || rawMessage;
          body = lines.slice(1).join('\n');
        }
 
        appendTimeline({
          kind,
          title,
          body: body || undefined,
          status: level === 'error' ? 'error' : level === 'warning' ? 'warning' : 'completed',
          level,
          category,
          task,
          tag: category,
          usesSecretTask: usesSecret,
          validationIssue: validationIssue || undefined,
        });
      });
 
      socket.on('llmThinking', (data) => {
        const node = data?.node || 'agent';
        const taskText = data?.task?.text || '';
        const usesSecret = instructionUsesSecretVariable(
          taskText,
          variablesList,
          variableDescriptions
        );
        // Plain-language titles only — never expose graph node names to testers
        const beat = THOUGHT_BEAT_BY_NODE[node] || (/recover/i.test(String(node)) ? 'recover' : 'think');
        appendTimeline({
          kind: 'thought',
          title: THOUGHT_BEAT_TITLE[beat],
          beat,
          body: sanitizeMindText(data?.thinking || ''),
          status: 'completed',
          category: data?.category || 'thought',
          task: toDisplayTask(data?.task),
          tag: 'thought',
          usesSecretTask: usesSecret,
        });
      });
 
      socket.on('testStep', (step) => {
        const raw = step?.step || step?.testStep || step;
        const taskText = raw?.instruction || raw?.description || '';
        const usesSecret = instructionUsesSecretVariable(
          taskText,
          variablesList,
          variableDescriptions
        );
        const titleRaw =
          raw?.description ||
          raw?.action ||
          raw?.name ||
          raw?.instruction ||
          raw?.outcome ||
          'Step completed';
        const title = sanitizeMindText(titleRaw);
        const tag = raw?.tool || raw?.type || raw?.tool_call || 'Action';
        const failed = raw?.passed === false || String(raw?.outcome || '').toLowerCase().includes('error');
        appendTimeline({
          kind: 'action',
          title,
          tag,
          status: failed ? 'error' : 'completed',
          body: [
            sanitizeMindText(raw?.outcome || raw?.reason || ''),
            raw?.pageUrl || raw?.page_url ? `Page: ${raw?.pageUrl || raw?.page_url}` : '',
            (() => {
              const code = raw?.playwrightCode || raw?.playwright_code || '';
              if (!code) return '';
              const firstLine = sanitizeMindText(String(code).split('\n')[0]);
              return firstLine ? `Code: ${firstLine}` : '';
            })(),
          ].filter(Boolean).join(' · '),
          category: raw?.category || 'action',
          task: toDisplayTask(raw?.task),
          tool: tag,
          outcome: sanitizeMindText(raw?.outcome || raw?.reason || ''),
          pageUrl: raw?.pageUrl || raw?.page_url || null,
          playwrightCode: convertIdsToDisplay(
            raw?.playwrightCode || raw?.playwright_code || '',
            variablesList,
            variableDescriptions
          ) || null,
          screenshotUrl: resolveScreenshotUrl(raw?.screenshotUrl || raw?.screenshot),
          stepNumber: raw?.stepNumber ?? raw?.step_number ?? null,
          highlightedRefs: raw?.highlightedRefs || raw?.highlighted_refs || [],
          instructionId: raw?.instructionId ?? raw?.instruction_id ?? null,
          usesSecretTask: usesSecret,
        });
 
        actionCountRef.current += 1;
        setStats((prev) => ({
          ...prev,
          actionsCurrent: actionCountRef.current,
          actionsTotal: Math.max(prev.actionsTotal, actionCountRef.current),
        }));
        setExecutorMeta((prev) => ({
          ...prev,
          actionCurrent: actionCountRef.current,
          actionTotal: Math.max(prev.actionTotal, actionCountRef.current),
          targetLabel: raw?.target || raw?.selector || prev.targetLabel,
          pageUrl: raw?.pageUrl || raw?.page_url || prev.pageUrl,
        }));
 
        const instructionId = raw?.instruction_id ?? raw?.instructionId;
        if (instructionId != null) {
          setInstructions((prev) =>
            prev.map((inst) => {
              const match = String(inst.id) === String(instructionId) || Number(inst.number) === Number(instructionId);
              if (!match) return inst;
              const child = {
                id: `act-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
                number: `${inst.number}.${(inst.children?.length || 0) + 1}`,
                text: title,
                status: failed ? 'error' : 'completed',
                tool: tag,
              };
              return {
                ...inst,
                status: failed ? 'error' : 'running',
                expanded: true,
                children: [...(inst.children || []), child],
              };
            })
          );
        }
 
        const shotUrl = resolveScreenshotUrl(raw?.screenshotUrl || raw?.screenshot);
        const pageUrl = raw?.pageUrl || raw?.page_url || null;
        if (shotUrl) {
          recentStepShotUrlsRef.current.add(shotUrl);
          const shot = {
            url: shotUrl,
            pageUrl,
            description: title,
            passed: !failed,
            at: toTimeString(),
          };
          setLatestScreenshot(shot);
          setScreenshots((prev) => [...prev, shot]);
        } else if (pageUrl) {
          setExecutorMeta((prev) => ({ ...prev, pageUrl }));
        }
      });
 
      socket.on('screenshot', (shot) => {
        const url = resolveScreenshotUrl(shot?.url || shot?.path || shot?.imageUrl || shot?.screenshotUrl);
        const pageUrl = shot?.pageUrl || shot?.page_url || null;
        if (pageUrl) {
          setExecutorMeta((prev) => ({ ...prev, pageUrl }));
        }
        if (!url) {
          if (pageUrl) {
            appendTimeline({
              kind: 'log',
              title: `Page updated · ${pageUrl}`,
              status: 'completed',
              category: 'progress',
              tag: 'progress',
              pageUrl,
              task: toDisplayTask(shot?.task),
            });
          }
          return;
        }
        const next = {
          url,
          pageUrl,
          description: shot?.description || '',
          passed: shot?.passed,
          at: toTimeString(),
          stepNumber: shot?.stepNumber,
        };
        setLatestScreenshot(next);
        setScreenshots((prev) => [...prev, next]);
        // Skip Mind duplicate when testStep already attached the same shot
        const dupOfStep =
          recentStepShotUrlsRef.current.has(url) && shot?.passed !== false;
        if (!dupOfStep) {
          appendTimeline({
            kind: 'action',
            title: shot?.description
              ? `Screenshot · ${shot.description}`
              : `Screenshot · step ${shot?.stepNumber || '?'}`,
            status: shot?.passed === false ? 'error' : 'completed',
            category: 'screenshot',
            tag: 'screenshot',
            task: toDisplayTask(shot?.task),
            pageUrl,
            screenshotUrl: url,
            stepNumber: shot?.stepNumber ?? null,
            highlightedRefs: shot?.highlightedRefs || [],
            body: pageUrl ? `Captured after action @ ${pageUrl}` : 'Captured after browser action',
          });
        }
      });
 
      socket.on('hierarchicalSteps', (data) => {
        const steps = data?.steps || [];
        if (!Array.isArray(steps) || steps.length === 0) return;
        const mapped = mapHierarchicalToInstructions(steps);
        setInstructions(mapped);
        const completed = mapped.filter((s) => s.status === 'completed').length;
        const runningIdx = mapped.findIndex((s) => s.status === 'running');
        setStats((prev) => ({
          ...prev,
          stepsCurrent: completed + (runningIdx >= 0 ? 1 : 0),
          stepsTotal: mapped.length,
        }));
        setExecutorMeta((prev) => ({
          ...prev,
          instructionCurrent: runningIdx >= 0 ? runningIdx + 1 : Math.min(completed + 1, mapped.length),
          instructionTotal: mapped.length,
        }));
      });
 
      socket.on('tokenUsage', (usage) => {
        setTokenUsage(usage);
      });
 
      socket.on('planSaveSuccess', (data) => {
        const id = data?.planId || '';
        if (id) {
          setPlanId(id);
          writeStoredPlanId(projectId, id);
        }
        appendTimeline({
          kind: 'log',
          title: `Plan saved: ${id || 'ok'}`,
          status: 'completed',
        });
      });
 
      socket.on('testComplete', (result) => {
        clearTimer();
        runEndedRef.current = true;
        const ok = isRunSuccessful(result);
        setRunLabel(ok ? RUN_LABEL.COMPLETED : RUN_LABEL.ERROR);
        setLastResult(result);
        setResultModalOpen(true);
        const used = Array.isArray(result?.variablesUsed) ? result.variablesUsed : [];
        setVariablesUsedRows(
          used.map((row) =>
            resolveVariablesUsedRow(row, variablesList, variableDescriptions)
          )
        );
        setStats((prev) => ({
          ...prev,
          variablesUsed: used.length || prev.variablesUsed,
        }));
        const errorCode = result?.errorCode || result?.error_code;
        if (errorCode && L5_CHIP_MESSAGES[errorCode]) {
          setConnectionError(L5_CHIP_MESSAGES[errorCode]);
        } else if (!ok && result?.error) {
          const errText = String(result.error);
          // Prefer a short chip — full reason lives in the timeline Details cards
          const firstLine = errText.split('\n').map((l) => l.trim()).find(Boolean) || errText;
          setConnectionError(
            firstLine.replace(/^What:\s*/i, '').slice(0, 160) || 'Run finished with errors'
          );
        }
        const completedPlanId = result?.plan_id || result?.planId || sessionIdRef.current;
        if (completedPlanId) {
          setPlanId(completedPlanId);
          writeStoredPlanId(projectId, completedPlanId);
        }
        const validationNodes = Array.isArray(result?.validationNodes)
          ? result.validationNodes
          : Array.isArray(result?.validation_nodes)
            ? result.validation_nodes
            : [];
        const summaryText = result?.summary || result?.error || result?.test_status || '';
        settleRunningTimeline(ok ? 'completed' : 'error');
        appendTimeline({
          kind: 'instruction',
          title: ok
            ? 'Run completed'
            : validationNodes.length
              ? 'Stopped before run — fix the steps above'
              : 'Run finished with errors',
          status: ok ? 'completed' : 'error',
          body: summaryText,
          validationNodes,
        });
        if (result?.tokenUsage) setTokenUsage(result.tokenUsage);
        setIsConnected(false);
        setTimeout(() => teardownSocket(), 250);
      });
 
      socket.on('error', (payload) => {
        const errorCode = payload?.errorCode || payload?.error_code;
        const message =
          (errorCode && L5_CHIP_MESSAGES[errorCode]) ||
          (typeof payload === 'string' ? payload : payload?.message || 'Agent error');
        setConnectionError(message);
        settleRunningTimeline('error');
        appendTimeline({
          kind: 'log',
          title: message,
          status: 'error',
          level: 'error',
        });
      });
 
      socket.on('disconnect', () => {
        setIsConnected(false);
        if (runEndedRef.current) return; // expected — already ended via testComplete/stop
        // Socket died mid-run with no testComplete/error — fail fast instead of leaving
        // the UI stuck showing "Running" with a timer that never stops.
        runEndedRef.current = true;
        clearTimer();
        setRunLabel(RUN_LABEL.ERROR);
        const failure = { success: false, error: 'Connection to agent lost — run did not finish.' };
        setLastResult(failure);
        setConnectionError(failure.error);
        setResultModalOpen(true);
        appendTimeline({ kind: 'log', title: failure.error, status: 'error', level: 'error' });
      });
    },
    [appendTimeline, clearTimer, projectId, sanitizeMindText, settleRunningTimeline, teardownSocket, toDisplayTask, variableDescriptions, variablesList]
  );
 
  const connectSocket = useCallback(async () => {
    teardownSocket();
    const auth = {};
    if (AUTOMATION_NEW_AGENT_TOKEN) auth.token = AUTOMATION_NEW_AGENT_TOKEN;
 
    const socket = io(AUTOMATION_NEW_AGENT_URL, {
      path: AUTOMATION_NEW_SOCKET_PATH,
      transports: ['websocket', 'polling'],
      withCredentials: false,
      forceNew: true,
      auth,
      reconnection: false,
    });
    socketRef.current = socket;
    bindListeners(socket);
 
    await new Promise((resolve, reject) => {
      socket.once('connect', resolve);
      socket.once('connect_error', reject);
    });
    setIsConnected(true);
    return socket;
  }, [bindListeners, teardownSocket]);
 
  const resetRunUi = useCallback((instructionCount) => {
    runEndedRef.current = false;
    setResultModalOpen(false);
    setConnectionError(null);
    setTimeline([]);
    recentStepShotUrlsRef.current = new Set();
    setLatestScreenshot(null);
    setScreenshots([]);
    setTokenUsage(null);
    setLastResult(null);
    setVariablesUsedRows([]);
    actionCountRef.current = 0;
    const metaCount = Object.keys(resolveVariablePayload()?.variableMetadata || {}).length;
    setStats({
      stepsCurrent: 0,
      stepsTotal: instructionCount,
      actionsCurrent: 0,
      actionsTotal: 0,
      retries: 0,
      variablesUsed: metaCount,
    });
    setExecutorMeta((prev) => ({
      ...prev,
      instructionCurrent: instructionCount > 0 ? 1 : 0,
      instructionTotal: instructionCount,
      actionCurrent: 0,
      actionTotal: 0,
      targetLabel: null,
      confidence: null,
      pageUrl: null,
    }));
  }, [resolveVariablePayload]);
 
  const stopRun = useCallback(() => {
    runEndedRef.current = true; // user-initiated — the disconnect that follows isn't a failure
    const socket = socketRef.current;
    if (socket?.connected) {
      socket.emit('stopTest', {});
    }
    clearTimer();
    setRunLabel(RUN_LABEL.STOPPED);
    settleRunningTimeline('warning');
    appendTimeline({ kind: 'log', title: 'Stop requested', status: 'warning', level: 'warning' });
    teardownSocket();
  }, [appendTimeline, clearTimer, settleRunningTimeline, teardownSocket]);

  const startRun = useCallback(async () => {
    const payloadInstructions = instructionsToPayload(instructions);
    if (payloadInstructions.length === 0) {
      setConnectionError('Add at least one instruction before running.');
      return;
    }
    // why gate before agent: fail closed on env/refs without starting a secret-bearing run
    const blockers = buildPreflightMessages({
      instructions,
      environmentId,
      variablesList,
      variableDescriptions,
      agentHealthy,
    });
    if (blockers.length) {
      setPreflightMessages(blockers);
      setConnectionError(blockers[0]);
      return;
    }
 
    secretScrubValuesRef.current = collectSecretPlaintexts(
      variablesList,
      environmentId,
      variableDescriptions
    );
    resetRunUi(payloadInstructions.length);
    setInstructions((prev) =>
      prev.map((inst, index) => ({
        ...inst,
        number: index + 1,
        status: index === 0 ? 'running' : 'pending',
        expanded: index === 0,
        children: [],
      }))
    );
    setRunLabel(RUN_LABEL.CONNECTING);
 
    const sessionId = createSessionId('record');
    sessionIdRef.current = sessionId;
 
    let socket;
    try {
      socket = await connectSocket();
    } catch (error) {
      setConnectionError(
        error?.message ||
          `Cannot connect to agent at ${AUTOMATION_NEW_AGENT_URL}. Start qa-path-automation-agent on :8000.`
      );
      setRunLabel(RUN_LABEL.ERROR);
      teardownSocket();
      return;
    }
 
    startTimer();
    setRunLabel(RUN_LABEL.RUNNING);
    appendTimeline({
      kind: 'instruction',
      title: `Record started · ${sessionId}`,
      status: 'running',
    });
 
    const variablePayload = resolveVariablePayload();
    socket.emit('executeTest', {
      sessionId,
      testId: sessionId,
      instructions: payloadInstructions,
      variablePayload,
      environmentId: environmentId || undefined,
      projectId: projectId || undefined,
      userId: userId || undefined,
      orgId: orgId || undefined,
    });
  }, [
    agentHealthy,
    appendTimeline,
    connectSocket,
    environmentId,
    instructions,
    orgId,
    projectId,
    resetRunUi,
    resolveVariablePayload,
    startTimer,
    teardownSocket,
    userId,
    variableDescriptions,
    variablesList,
  ]);
 
  const startReplay = useCallback(async () => {
    const id = planId?.trim();
    if (!id) {
      setConnectionError('No planId yet. Run a successful record first (plan_id comes from testComplete).');
      return;
    }
    const blockers = buildPreflightMessages({
      instructions,
      environmentId,
      variablesList,
      variableDescriptions,
      agentHealthy,
    });
    if (blockers.length) {
      setPreflightMessages(blockers);
      setConnectionError(blockers[0]);
      return;
    }
 
    secretScrubValuesRef.current = collectSecretPlaintexts(
      variablesList,
      environmentId,
      variableDescriptions
    );
    resetRunUi(instructions.length || 1);
    setInstructions((prev) =>
      prev.map((inst, index) => ({
        ...inst,
        number: index + 1,
        status: index === 0 ? 'running' : 'pending',
        expanded: index === 0,
        children: [],
      }))
    );
    setRunLabel(RUN_LABEL.CONNECTING);
    const sessionId = createSessionId('replay');
    sessionIdRef.current = sessionId;
 
    let socket;
    try {
      socket = await connectSocket();
    } catch (error) {
      setConnectionError(error?.message || `Cannot connect to agent at ${AUTOMATION_NEW_AGENT_URL}`);
      setRunLabel(RUN_LABEL.ERROR);
      teardownSocket();
      return;
    }
 
    startTimer();
    setRunLabel(RUN_LABEL.RUNNING);
    appendTimeline({
      kind: 'instruction',
      title: `Replay started · plan ${id}`,
      status: 'running',
    });
 
    socket.emit('replayTestPlanJson', {
      sessionId,
      testId: sessionId,
      planId: id,
      plan_id: id,
      variablePayload: resolveVariablePayload(),
      projectId: projectId || undefined,
      userId: userId || undefined,
      orgId: orgId || undefined,
      environmentId: environmentId || undefined,
    });
  }, [
    agentHealthy,
    appendTimeline,
    connectSocket,
    environmentId,
    instructions,
    orgId,
    planId,
    projectId,
    userId,
    resetRunUi,
    resolveVariablePayload,
    startTimer,
    teardownSocket,
    variableDescriptions,
    variablesList,
  ]);
 
  const savePlan = useCallback(() => {
    const socket = socketRef.current;
    const id = planId || sessionIdRef.current;
    if (!socket?.connected) {
      // Persist locally even if socket already closed after testComplete
      if (id) {
        writeStoredPlanId(projectId, id);
        setPlanId(id);
        appendTimeline({ kind: 'log', title: `Plan id stored: ${id}`, status: 'completed' });
        return;
      }
      setConnectionError('No plan to save yet. Complete a record run first.');
      return;
    }
    socket.emit('savePlan', { planName: id, planId: id, sessionId: sessionIdRef.current });
  }, [appendTimeline, planId, projectId]);
 
  const clearInstructions = useCallback(() => {
    setInstructions([
      {
        id: `inst-${Date.now()}`,
        number: 1,
        text: '',
        status: 'pending',
        expanded: false,
        children: [],
      },
    ]);
  }, []);
 
  const replaceInstructions = useCallback((next) => {
    if (!Array.isArray(next) || next.length === 0) {
      clearInstructions();
      return;
    }
    setInstructions(
      next.map((inst, index) => ({
        id: inst.id || `inst-${Date.now()}-${index}`,
        number: index + 1,
        text: inst.text ?? '',
        status: inst.status || 'pending',
        expanded: Boolean(inst.expanded),
        children: Array.isArray(inst.children) ? inst.children : [],
      }))
    );
  }, [clearInstructions]);
 
  const filteredTimeline = useMemo(() => {
    // Legacy filter ids still accepted
    if (timelineFilter === 'all' || timelineFilter === 'all_events') return timeline;
    if (timelineFilter === 'thoughts') {
      return timeline.filter((e) => e.kind === 'thought' || e.category === 'thought' || e.category === 'directive');
    }
    if (timelineFilter === 'logs') {
      return timeline.filter((e) =>
        e.kind === 'log' ||
        e.kind === 'instruction' ||
        ['progress', 'evaluation', 'recover', 'guardrail', 'validation', 'system'].includes(e.category)
      );
    }
    if (timelineFilter === 'actions') {
      return timeline.filter((e) => e.kind === 'action' || e.category === 'action' || e.category === 'screenshot');
    }
    if (timelineFilter === 'problems') {
      return timeline.filter(
        (e) =>
          e.status === 'error' ||
          e.status === 'warning' ||
          e.category === 'recover' ||
          (e.category === 'evaluation' && e.status !== 'completed')
      );
    }
    // story (default): plan → act → result; drop progress noise + happy screenshots
    return timeline.filter((e) => {
      if (e.category === 'progress') return false;
      if (e.category === 'screenshot') return e.status === 'error';
      if (e.kind === 'thought' || e.category === 'thought') {
        // Narrative beats (plan / check / recover) plus secret notices carry the story
        return Boolean(e.beat) || e.category === 'directive' || e.category === 'evaluation';
      }
      if (e.category === 'validation') return true;
      if (e.category === 'guardrail') {
        return e.status === 'error' || e.status === 'warning' || e.level === 'warning' || e.level === 'error';
      }
      return true;
    });
  }, [timeline, timelineFilter]);
 
  const dismissResultModal = useCallback(() => setResultModalOpen(false), []);
 
  const runStatus = useMemo(
    () => ({
      label: runLabel,
      currentStep: executorMeta.instructionCurrent || stats.stepsCurrent,
      totalSteps: executorMeta.instructionTotal || stats.stepsTotal || instructions.length,
      elapsed,
    }),
    [elapsed, executorMeta, instructions.length, runLabel, stats]
  );
 
  return {
    agentUrl: AUTOMATION_NEW_AGENT_URL,
    agentHealthy,
    instructions,
    clearInstructions,
    replaceInstructions,
    variableSuggestions,
    variableDescriptions,
    fileSuggestions,
    fileDescriptions,
    refreshFiles,
    timeline: filteredTimeline,
    timelineLength: timeline.length,
    timelineFilter,
    setTimelineFilter,
    stats,
    runStatus,
    isRunning,
    isConnected,
    connectionError,
    latestScreenshot,
    screenshots,
    tokenUsage,
    lastResult,
    resultModalOpen,
    dismissResultModal,
    planId,
    setPlanId,
    environments,
    environmentId,
    setEnvironmentId,
    variablePayloadText,
    setVariablePayloadText,
    preflightMessages,
    runBlocked,
    variablesUsedRows,
    executorMeta,
    startRun,
    startReplay,
    stopRun,
    savePlan,
    checkAgentHealth,
  };
}